import { useMemo } from 'react';
import { useSnackbar } from 'notistack';
import { useQuery } from '@tanstack/react-query';

import BeneficiaryService, {
  type BeneficiariesServiceProps,
} from '@services/BeneficiaryService';

import { useAuth, useFilterParams } from '@hooks';
import { ITEMS_LIMIT_PER_PAGE } from '@constants';
import { toastifyApiErrors } from '@utils';
import { BeneficiaryQueryKeysEnum } from 'constants/private/beneficiaries/beneficiaryQueryKeys';

export function useBeneficiaryServices(
  beneficiaryCode?: string,
  isOpenModalRedirect?: boolean,
) {
  const { currentPage, searchParam, tableOrder, mappedFilters } =
    useFilterParams();

  const { selectedCompany, selectedPartnerCompany } = useAuth();
  const beneficiaryService = useMemo(() => new BeneficiaryService(), []);
  const companyId = selectedCompany?.id ?? '';
  const partnerCompany = selectedPartnerCompany?.partnerCompany ?? '';
  const { enqueueSnackbar } = useSnackbar();

  const beneficiariesServiceList = useQuery<BeneficiariesServiceProps>({
    queryKey: [
      BeneficiaryQueryKeysEnum.BENEFICIARY_SERVICES_LIST_STATE,
      mappedFilters,
      searchParam,
      currentPage,
      tableOrder,
      companyId,
      partnerCompany,
    ],
    queryFn: () =>
      beneficiaryService.fetchBeneficiariesServices({
        companyId,
        ...mappedFilters,
        ...searchParam,
        ...tableOrder,
        offset: ITEMS_LIMIT_PER_PAGE * (currentPage - 1),
        limit: ITEMS_LIMIT_PER_PAGE,
      }),
    enabled: Boolean(searchParam.search),
    onError: error => {
      toastifyApiErrors(error, enqueueSnackbar);
    },
  });

  const beneficiariesServiceRedirect = useQuery<BeneficiariesServiceProps>({
    queryKey: [
      BeneficiaryQueryKeysEnum.BENEFICIARY_SERVICES_REDIRECT_STATE,
      beneficiaryCode,
    ],
    queryFn: () =>
      beneficiaryService.getRedirectBeneficiaryService({
        beneficiaryCode: beneficiaryCode ?? '',
      }),
    enabled: Boolean(beneficiaryCode) && isOpenModalRedirect,
    onError: error => {
      toastifyApiErrors(error, enqueueSnackbar);
    },
    refetchOnMount: false,
  });

  const refreshBeneficiariesServiceList = () => {
    beneficiariesServiceList.remove();

    beneficiariesServiceList.refetch();
  };

  const isSearchEmpty = !searchParam.search;

  const isResultEmpty = beneficiariesServiceList.data?.total === 0;

  return {
    beneficiariesServiceList,
    beneficiariesServiceRedirect,
    isSearchEmpty,
    isResultEmpty,
    refreshBeneficiariesServiceList,
  };
}
